export const MARKETING = {
  product: "Videra PM",
  legalName: "Videra Technology Limited",
  salesEmail: "hello@videra.app",
  company: {
    name: "Videra Technology Limited",
    number: "17380198",
    addressLines: [
      "2nd Floor College House",
      "17 King Edwards Road",
      "Ruislip",
      "London HA4 7AE",
      "United Kingdom",
    ],
    email: "hello@videra.app",
    jurisdiction: "Registered in England and Wales",
  },
  tagline: "Your projects. Every one. Under control.",
  strapline: "Operational Excellence — powered by Videra Technology",
  sub:
    "Videra PM gives PMOs, project teams, and delivery partners a single place to track, govern, and report on every project, programme, and portfolio — and a record that proves what you knew, when you knew it, and who signed it off.",
  problem: {
    heading: "Sound familiar?",
    pairs: [
      {
        pain: "Your exec sponsor asks for a RAG update and you spend two hours pulling it together manually",
        fix: "The report already exists. Status, exposure and slippage are generated from the records your team already keep.",
      },
      {
        pain: "Risks sit in spreadsheets nobody reads until they become issues",
        fix: "Live registers with owners, review dates and automatic chase-ups — the platform chases, not you.",
      },
      {
        pain: "A partner marks their own homework: green, green, green, then catastrophically red",
        fix: "Dependencies are accepted or rejected on the record, and every status goes through approval with a trail.",
      },
      {
        pain: "Six months on, nobody remembers who agreed what — and it lands on you",
        fix: "Decisions, gates and approvals are captured with who, when and why. A defensible position, not a memory.",
      },
      {
        pain: "Every client or department runs projects differently, with no consistent framework",
        fix: "Frameworks, gates and templates applied once, inherited by every programme after it.",
      },
      {
        pain: "You win the contract, then scramble to set up the delivery infrastructure",
        fix: "Sign up and your configured, branded workspace is live the same afternoon.",
      },
    ],
  },
  whatItDoes: {
    overline: "What it does",
    heading: "Built for organisations that deliver at scale",
    body:
      "Videra PM is a project delivery platform built for organisations that need to deliver at scale. One platform covering the full delivery lifecycle, from initiation through gate reviews to handback. Configurable for your sector, brandable for your clients.",
  },
  roles: {
    overline: "Who it answers to",
    heading: "One record, four different questions",
    items: [
      ["Executive sponsor / members", "One page, every programme, refreshed live. No pack required."],
      ["Programme director", "Dependencies and risks surface before they cost you money."],
      ["PMO lead", "Every programme run the same way, without policing it."],
      ["IT & information governance", "Tenant-bound AI, redacted egress, assurance statement in the box."],
    ],
  },
  sectors: [
    {
      icon: "Landmark",
      title: "Place-based & infrastructure programmes",
      body:
        "You have been handed eleven projects, four partners and a strategic outcome to prove. Bring them into one integrated programme, track the regeneration and investment benefits promised in the business case through to realised, and brief members from the same live record.",
      span: "lg:col-span-7",
    },
    {
      icon: "ClipboardCheck",
      title: "Operational readiness",
      body:
        "Go-live is fixed and readiness lives in one person's spreadsheet. Gate-controlled stages, evidence attached at each gate, and a readiness position you can defend in front of a board rather than reconstruct the night before.",
      span: "lg:col-span-5",
    },
    {
      icon: "HardHat",
      title: "Construction",
      body:
        "You inherit a scheme mid-flight with no gate record. Rebuild a defensible position in an afternoon — stages, variations, decisions and handback readiness in one place, in your own terminology.",
      span: "lg:col-span-5",
    },
    {
      icon: "HeartPulse",
      title: "Healthcare",
      body:
        "Capital schemes, compliance evidence and an executive who wants one page. Portfolio visibility without a monthly consolidation exercise, and an assurance pack your information governance team will accept.",
      span: "lg:col-span-7",
    },
    {
      icon: "Server",
      title: "IT & transformation",
      body:
        "Change requests arrive faster than they can be assessed. Sprint gates, technical RAID and change control that keeps pace, with dependencies visible across every other workstream in the programme.",
      span: "lg:col-span-7",
    },
    {
      icon: "Briefcase",
      title: "Professional services & delivery partners",
      body:
        "A new client expects your delivery infrastructure on day one. White-label a branded workspace per client, run them all on the same governance, and stop rebuilding it from scratch for every engagement.",
      span: "lg:col-span-5",
    },
  ],
  consistency: {
    overline: "Convenience & consistency",
    heading: "Every programme run the same way, without anyone having to police it",
    body:
      "Governance stops being a document people ignore and becomes the way the tool works. Set it up once and every programme, client and department after it inherits the same structure — so a new project manager is productive on their first Monday.",
    points: [
      "Framework configs apply your gates, stages and required evidence to any project in one action",
      "Schedule templates turn a plan that worked into the plan you reuse",
      "14 sector presets change the terminology throughout — Schemes and Variations, or Projects and Change Controls",
      "Reporting periods open and close on a cycle, with overdue updates chased automatically",
      "Roll-up from project to programme to portfolio is a consequence of the data, not a monthly exercise",
      "Status reports, progress packs and board exports generated as PDF on demand",
    ],
    footnote: "Live the same afternoon. No implementation project, no consultancy, no six-month mobilisation.",
  },
  defensibility: {
    overline: "The difference",
    heading: "The record you can stand behind",
    body:
      "Every platform gives you a dashboard. Videra gives you the thing you actually need when a programme is questioned: proof of what was known, when, by whom, and what was done about it.",
    points: [
      ["Decisions on the record", "Who decided, when, on what basis — captured as it happens, not reconstructed afterwards."],
      ["Approval trails", "Every status update and gate carries its requester, approver, comments and timestamps."],
      ["Dependencies that answer back", "The other side accepts or rejects in the system, so nobody can claim they were never asked."],
      ["Benefits tracked to realised", "The outcomes promised in the business case, followed through with evidence."],
      ["Compliance scorecards", "Where governance is complete, where it is thin, and exactly which record is missing."],
      ["Full audit trail and AI log", "Including every assisted call, its redacted prompt and its response."],
    ],
    close:
      "This is what makes you promotable rather than indispensable: the programme stays legible when you are not in the room.",
  },
  cta: {
    heading: "Live in minutes, not months.",
    body:
      "Sign up, pay, and have your own configured dashboard live in minutes. No setup, no waiting, no implementation project required.",
  },
  proofPoints: [
    { value: "14", label: "Sector configurations" },
    { value: "7-day", label: "Free trial, no card" },
    { value: "100%", label: "Functional with AI switched off" },
  ],
  assurances: [
    "UK / EU data residency",
    "Tenant-bound AI, or AI switched off entirely",
    "Full approval and decision audit trail",
    "Role-based access, workspace isolation",
  ],
  security: {
    overline: "Data boundary",
    title: "Your AI, your tenant, your evidence",
    body:
      "Most platforms give you an AI feature and leave you to explain it to your information governance team. Videra PM lets you point the AI at your own Azure OpenAI resource, so redacted payloads never leave your tenant, region or data-processing agreement.",
    points: [
      "Bring your own Azure OpenAI endpoint — tenant-bound by design",
      "Internal-AI-only lock refuses external calls rather than falling back",
      "Secrets, emails, phone numbers, monetary values and identifiers redacted before egress",
      "Named staff pseudonymised so reports still read correctly",
      "Every call logged with its redacted prompt, response and digest",
      "AI Assurance Statement PDF you can hand straight to IT",
    ],
    footnote:
      "And if the answer is simply no: switch AI off entirely. Every other feature keeps working.",
  },
  faqs: [
    {
      q: "Does the platform still work if we switch AI off?",
      a:
        "Yes, completely. AI assists six features — report drafting, status drafts, classification, mitigation suggestions, triage and chase-up summaries — and each falls back to manual entry. Every register, plan, approval, report and dashboard is unaffected.",
    },
    {
      q: "Where is our data processed?",
      a:
        "Your workspace data stays in the platform database. If you enable AI, you choose the engine: your own Azure OpenAI resource, which keeps processing inside your tenant and region, or a shared gateway. The AI Assurance Statement documents whichever you pick.",
    },
    {
      q: "Can we use our own branding and terminology?",
      a:
        "Yes. Professional and Enterprise include full white-labelling — logo, colours and product name — plus sector presets that change the language throughout, so a construction client sees Schemes and Variations while an NHS client sees Projects and Change Controls.",
    },
    {
      q: "What happens at the end the trial?",
      a:
        "Your workspace becomes read-only. Nothing is deleted, and everything you created is waiting for you as soon as you choose a plan.",
    },
    {
      q: "Can we migrate from spreadsheets or SharePoint?",
      a:
        "Yes. The platform was rebuilt from a legacy SharePoint and Power Platform governance tool, so the structures map closely. Import routes are available and Enterprise includes guided onboarding.",
    },
    {
      q: "Are you available on public sector frameworks?",
      a:
        "We are working towards a G-Cloud Digital Marketplace listing so public sector buyers can procure through an existing framework. Talk to us about your route to purchase in the meantime.",
    },
  ],
};
